import React from "react";
import cn from "classnames";
import { Button, ButtonSize, ButtonType } from "@components/new/Button";
import { LinkButton } from "@components/new/LinkButton";
import { SubTitle3 } from "@components/new/Typo";
import NeutralIcon from "@assets/icons/solid/circle-info.svg?react";
import SuccessIcon from "@assets/icons/solid/circle-check.svg?react";
import WarningIcon from "@assets/icons/solid/circle-alert.svg?react";
import ErrorIcon from "@assets/icons/solid/circle-x.svg?react";
import { AlertType } from "./types";
import styles from './Alert.module.scss';

interface AlertProps {
  type?: AlertType;
  title?: React.ReactNode;
  description?: React.ReactNode;
  button?: {
    type?: string,
    title: string,
    icon?: string | React.FunctionComponent<React.SVGAttributes<SVGElement>>;
    suffix?: string | React.FunctionComponent<React.SVGAttributes<SVGElement>>;
    onClick: () => void;
  };
  isWide?: boolean;
}

function getIcon(type: AlertType) {
  const icons: Record<AlertType, React.FunctionComponent<React.SVGAttributes<SVGElement>>> = {
    [AlertType.NEUTRAL]: NeutralIcon,
    [AlertType.SUCCESS]: SuccessIcon,
    [AlertType.WARNING]: WarningIcon,
    [AlertType.ERROR]: ErrorIcon,
  }

  return icons[type] ?? NeutralIcon;
}

export const Alert: React.FC<AlertProps> = ({
  type,
  title,
  description,
  button,
  isWide,
}) => {
  const alertType = type ?? AlertType.NEUTRAL;
  const Icon = getIcon(alertType);

  return (
    <div className={cn(styles.alert, isWide && styles.alertWide, styles[`type-${alertType}`])}>
      <Icon className={styles.icon} />
      <div className={cn(styles.titleColumn, (!description && button) && styles.titleColumnNoDescription)}>
        <SubTitle3
          dangerouslySetInnerHTML={(typeof title === "string") ?
            { __html: title ?? "" } : undefined
          }
          children={(typeof title === "string") ? undefined : title}
        />
        {description ? (
          <SubTitle3
            color="grey-800"
            dangerouslySetInnerHTML={(typeof description === "string") ?
              { __html: description ?? "" } : undefined
            }
            children={(typeof description === "string") ? undefined : description}
          />
        ) : null}
      </div>
      {button ? (
        <div className={styles.buttons}>
          {button.type === "link" ? (
            <LinkButton
              title={button?.title}
              onClick={button?.onClick}
            />
          ) : (
            <Button
              size={ButtonSize.M}
              type={ButtonType.PRIMARY}
              title={button?.title}
              icon={button?.icon}
              suffix={button?.suffix}
              onClick={button?.onClick}
            />
          )}
        </div>
      ) : null}
    </div>
  );
};
